// hooks/useMilitarySessionHandlers.js
import { useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from '../../../components/Common/Toast';
import config from '../../../config';

export const useMilitarySessionHandlers = (
  token,
  user,
  selectedTeam,
  setShowMilitaryVideoCall,
  setMilitarySessionId,
  setActiveMilitarySession,
  setIsMilitaryCallMinimized,
  setVideoCallSelectedUser,
  setPendingMilitarySession,
  setShowMilitarySessionConfirm,
  teamMembers,
  fetchTeamMembers,
  onStartMilitaryProgram // New parameter for batch handling
) => {

  const isEndingSessionRef = useRef(false);
  const activeSessionIdRef = useRef(null);

  const checkUserActiveMilitarySession = useCallback(async ({ skipIfInterviewPending = false } = {}) => {
    try {
      const response = await axios.get(
        `${config.baseURL}/api/military/user/active-session`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.has_active_session) {
        const session = response.data.active_session;

        if (session.answered_count >= session.total_questions) {
          console.log("📋 Session already completed, skipping invitation");
          return null;
        }

        if (session.status === 'completed' || session.status === 'cancelled') {
          console.log("📋 Session status is", session.status, "- skipping invitation");
          return null;
        }

        if (skipIfInterviewPending) {
          console.log("⏭️ Skipping military popup — regular interview check takes priority");
          return session;
        }

        setPendingMilitarySession(session);
        setShowMilitarySessionConfirm(true);
        return session;
      } else {
        return null;
      }
    } catch (error) {
      console.error("❌ Error checking active military session:", error);
      return null;
    }
  }, [token, setPendingMilitarySession, setShowMilitarySessionConfirm]);

  const handleStartMilitarySession = useCallback(async (sessionConfig) => {
    try {
      if (sessionConfig.program_id) {
        const response = await axios.post(
          `${config.baseURL}/api/military/programs/${sessionConfig.program_id}/sessions/week/${sessionConfig.week_number}/start`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          const session = response.data.session;
          setActiveMilitarySession(session);
          setMilitarySessionId(session.session_id);
          activeSessionIdRef.current = session.session_id;
          setShowMilitaryVideoCall(true);
          setIsMilitaryCallMinimized(false);

          const selectedMember = teamMembers.find(m => m.user_id === sessionConfig.user_id);
          setVideoCallSelectedUser(selectedMember || {
            first_name: sessionConfig.candidate_name || 'Candidate',
            last_name: '',
            user_id: sessionConfig.user_id
          });

          return { success: true, session };
        }
      } else {
        const response = await axios.post(
          `${config.baseURL}/api/military/teams/${selectedTeam.team_id}/start-program`,
          { user_id: sessionConfig.user_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          toast.success('Military assessment program started!');

          const sessionResponse = await axios.post(
            `${config.baseURL}/api/military/programs/${response.data.program.program_id}/sessions/week/1/start`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (sessionResponse.data.success) {
            const session = sessionResponse.data.session;
            setActiveMilitarySession(session);
            setMilitarySessionId(session.session_id);
            activeSessionIdRef.current = session.session_id;
            setShowMilitaryVideoCall(true);
            setIsMilitaryCallMinimized(false);

            const selectedMember = teamMembers.find(m => m.user_id === sessionConfig.user_id);
            setVideoCallSelectedUser(selectedMember || {
              first_name: sessionConfig.candidate_name || 'Candidate',
              last_name: '',
              user_id: sessionConfig.user_id
            });

            return { success: true, session, program: response.data.program };
          }
        }
      }

      throw new Error('Failed to start session');
    } catch (error) {
      console.error('Error starting military session:', error);
      toast.error(error.response?.data?.message || 'Failed to start assessment');
      return { success: false, error: error.message };
    }
  }, [token, selectedTeam, teamMembers, setActiveMilitarySession, setMilitarySessionId,
    setShowMilitaryVideoCall, setIsMilitaryCallMinimized, setVideoCallSelectedUser]);

  /**
   * FIXED: Handle both single and multiple candidates from AddMemberModal
   */
  const handleStartMilitaryFromAddModal = useCallback(async (data) => {
    try {
      console.log('🎯 handleStartMilitaryFromAddModal received:', data);
      console.log('🔑 Batch ID from data:', data.batchId);
      
      // Check if this is a batch assessment (multiple candidates)
      if (data.candidates && Array.isArray(data.candidates) && data.candidates.length > 1) {
        // Multiple candidates - batch assessment
        console.log('📦 Batch assessment with', data.candidates.length, 'candidates, batchId:', data.batchId);
        
        if (onStartMilitaryProgram) {
          // Pass the EXACT same data without modification
          onStartMilitaryProgram(data);
        }
        return { success: true, isBatch: true };
      }
      
      // Single candidate - check both possible data structures
      const candidateData = data.candidates?.[0] || data;
      const session = data.session;
      const selectedFriend = data.selectedFriend || candidateData;
      
      if (!session) {
        console.error('No session data provided');
        toast.error('Failed to start assessment session');
        return { success: false, error: 'No session data' };
      }

      setActiveMilitarySession(session);
      setMilitarySessionId(session.session_id);
      activeSessionIdRef.current = session.session_id;
      setShowMilitaryVideoCall(true);
      setIsMilitaryCallMinimized(false);

      setVideoCallSelectedUser({
        first_name: selectedFriend?.first_name || selectedFriend?.candidate_name || 'Candidate',
        last_name: selectedFriend?.last_name || '',
        user_id: selectedFriend?.user_id
      });

      toast.success('Week 1 assessment started!');
      return { success: true, isBatch: false };
    } catch (error) {
      console.error('Error starting military session from modal:', error);
      toast.error('Failed to start assessment session');
      return { success: false, error: error.message };
    }
  }, [setActiveMilitarySession, setMilitarySessionId, setShowMilitaryVideoCall,
    setIsMilitaryCallMinimized, setVideoCallSelectedUser, onStartMilitaryProgram]);

  const handleJoinMilitarySession = useCallback((pendingSession) => {
    if (!pendingSession) return false;

    setActiveMilitarySession({
      session_id: pendingSession.session_id,
      program_id: pendingSession.program_id,
      week_number: pendingSession.week_number
    });
    setMilitarySessionId(pendingSession.session_id);
    activeSessionIdRef.current = pendingSession.session_id;

    setVideoCallSelectedUser({
      first_name: 'Assessment',
      last_name: `Week ${pendingSession.week_number}`,
      user_id: user?.id
    });

    setShowMilitaryVideoCall(true);
    setIsMilitaryCallMinimized(false);
    setShowMilitarySessionConfirm(false);
    setPendingMilitarySession(null);

    return true;
  }, [user, setActiveMilitarySession, setMilitarySessionId, setShowMilitaryVideoCall,
    setIsMilitaryCallMinimized, setVideoCallSelectedUser, setShowMilitarySessionConfirm,
    setPendingMilitarySession]);

  const handleMilitarySessionComplete = useCallback(async (result) => {
    console.log("✅ Military session completed:", result);

    setShowMilitaryVideoCall(false);
    setMilitarySessionId(null);
    setActiveMilitarySession(null);
    activeSessionIdRef.current = null;
    setIsMilitaryCallMinimized(false);

    if (result.program_completed) {
      toast.success('🎉 Assessment Program Completed!');
    } else {
      toast.success(`Week ${result.completed_week || '?'} completed!`);
    }

    if (selectedTeam && fetchTeamMembers) {
      fetchTeamMembers(selectedTeam.team_id);
    }

    return result;
  }, [selectedTeam, fetchTeamMembers, setShowMilitaryVideoCall, setMilitarySessionId,
    setActiveMilitarySession, setIsMilitaryCallMinimized]);

  const handleMilitarySessionClose = useCallback(() => {
    setShowMilitaryVideoCall(false);
    setMilitarySessionId(null);
    setActiveMilitarySession(null);
    activeSessionIdRef.current = null;
    setIsMilitaryCallMinimized(false);
    setVideoCallSelectedUser(null);

    if (selectedTeam && fetchTeamMembers) {
      fetchTeamMembers(selectedTeam.team_id);
    }
  }, [selectedTeam, fetchTeamMembers, setShowMilitaryVideoCall, setMilitarySessionId,
    setActiveMilitarySession, setIsMilitaryCallMinimized, setVideoCallSelectedUser]);

  const abandonSession = useCallback(async (sessionId) => {
    if (!sessionId) return { success: false };
    try {
      const response = await axios.post(
        `${config.baseURL}/api/military/sessions/${sessionId}/abandon`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error abandoning session:', error);
      return { success: false, error: error.message };
    }
  }, [token]);

  return {
    activeSessionIdRef,
    isEndingSessionRef,
    checkUserActiveMilitarySession,
    handleStartMilitarySession,
    handleStartMilitaryFromAddModal,
    handleJoinMilitarySession,
    handleMilitarySessionComplete,
    handleMilitarySessionClose,
    abandonSession,
  };
};





// // hooks/useMilitarySessionHandlers.js
// import { useCallback, useRef } from 'react';
// import axios from 'axios';
// import { toast } from '../../../components/Common/Toast';
// import config from '../../../config';

// export const useMilitarySessionHandlers = (
//   token,
//   user,
//   selectedTeam,
//   setShowMilitaryVideoCall,
//   setMilitarySessionId,
//   setActiveMilitarySession,
//   setIsMilitaryCallMinimized,
//   setVideoCallSelectedUser,
//   setPendingMilitarySession,
//   setShowMilitarySessionConfirm,
//   teamMembers,
//   fetchTeamMembers
// ) => {

//   const isEndingSessionRef = useRef(false);
//   const activeSessionIdRef = useRef(null);

//   /**
//    * Check if user has an active military session (for invitation popup)
//    * FIX: Only show popup if:
//    *  - session exists and is NOT completed
//    *  - session status is 'active' or 'pending' (not 'completed'/'cancelled')
//    *  - We pass a callback so Teams.jsx can gate this behind "no regular interview pending"
//    */
//   const checkUserActiveMilitarySession = useCallback(async ({ skipIfInterviewPending = false } = {}) => {
//     try {
//       // console.log("🔍 Checking for active military session...");
//       const response = await axios.get(
//         `${config.baseURL}/api/military/user/active-session`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       if (response.data.success && response.data.has_active_session) {
//         const session = response.data.active_session;
//         console.log("✅ Found active military session:", session);

//         // Don't show if session is completed (all questions answered)
//         if (session.answered_count >= session.total_questions) {
//           console.log("📋 Session already completed, skipping invitation");
//           return null;
//         }

//         // FIX: Don't show if session status is completed or cancelled
//         if (session.status === 'completed' || session.status === 'cancelled') {
//           console.log("📋 Session status is", session.status, "- skipping invitation");
//           return null;
//         }

//         // FIX: If caller says skip when regular interview is pending, do so
//         if (skipIfInterviewPending) {
//           console.log("⏭️ Skipping military popup — regular interview check takes priority");
//           return session; // return but don't show popup
//         }

//         setPendingMilitarySession(session);
//         setShowMilitarySessionConfirm(true);
//         return session;
//       } else {
//         // console.log("ℹ️ No active military session found");
//         return null;
//       }
//     } catch (error) {
//       console.error("❌ Error checking active military session:", error);
//       return null;
//     }
//   }, [token, setPendingMilitarySession, setShowMilitarySessionConfirm]);

//   /**
//    * Start a military session (called from MemberCard or AddMemberModal)
//    */
//   const handleStartMilitarySession = useCallback(async (sessionConfig) => {
//     try {
//       if (sessionConfig.program_id) {
//         const response = await axios.post(
//           `${config.baseURL}/api/military/programs/${sessionConfig.program_id}/sessions/week/${sessionConfig.week_number}/start`,
//           {},
//           { headers: { Authorization: `Bearer ${token}` } }
//         );

//         if (response.data.success) {
//           const session = response.data.session;
//           setActiveMilitarySession(session);
//           setMilitarySessionId(session.session_id);
//           activeSessionIdRef.current = session.session_id;
//           setShowMilitaryVideoCall(true);
//           setIsMilitaryCallMinimized(false);

//           const selectedMember = teamMembers.find(m => m.user_id === sessionConfig.user_id);
//           setVideoCallSelectedUser(selectedMember || {
//             first_name: sessionConfig.candidate_name || 'Candidate',
//             last_name: '',
//             user_id: sessionConfig.user_id
//           });

//           return { success: true, session };
//         }
//       } else {
//         const response = await axios.post(
//           `${config.baseURL}/api/military/teams/${selectedTeam.team_id}/start-program`,
//           { user_id: sessionConfig.user_id },
//           { headers: { Authorization: `Bearer ${token}` } }
//         );

//         if (response.data.success) {
//           toast.success('Military assessment program started!');

//           const sessionResponse = await axios.post(
//             `${config.baseURL}/api/military/programs/${response.data.program.program_id}/sessions/week/1/start`,
//             {},
//             { headers: { Authorization: `Bearer ${token}` } }
//           );

//           if (sessionResponse.data.success) {
//             const session = sessionResponse.data.session;
//             setActiveMilitarySession(session);
//             setMilitarySessionId(session.session_id);
//             activeSessionIdRef.current = session.session_id;
//             setShowMilitaryVideoCall(true);
//             setIsMilitaryCallMinimized(false);

//             const selectedMember = teamMembers.find(m => m.user_id === sessionConfig.user_id);
//             setVideoCallSelectedUser(selectedMember || {
//               first_name: sessionConfig.candidate_name || 'Candidate',
//               last_name: '',
//               user_id: sessionConfig.user_id
//             });

//             return { success: true, session, program: response.data.program };
//           }
//         }
//       }

//       throw new Error('Failed to start session');
//     } catch (error) {
//       console.error('Error starting military session:', error);
//       toast.error(error.response?.data?.message || 'Failed to start assessment');
//       return { success: false, error: error.message };
//     }
//   }, [token, selectedTeam, teamMembers, setActiveMilitarySession, setMilitarySessionId,
//     setShowMilitaryVideoCall, setIsMilitaryCallMinimized, setVideoCallSelectedUser]);

//   /**
//    * Start military session from AddMemberModal
//    */
//   const handleStartMilitaryFromAddModal = useCallback(async (data) => {
//     try {
//       const { program, session, selectedFriend } = data;

//       setActiveMilitarySession(session);
//       setMilitarySessionId(session.session_id);
//       activeSessionIdRef.current = session.session_id;
//       setShowMilitaryVideoCall(true);
//       setIsMilitaryCallMinimized(false);

//       setVideoCallSelectedUser({
//         first_name: selectedFriend?.first_name || 'Candidate',
//         last_name: selectedFriend?.last_name || '',
//         user_id: selectedFriend?.user_id
//       });

//       toast.success('Week 1 assessment started!');
//       return { success: true };
//     } catch (error) {
//       console.error('Error starting military session from modal:', error);
//       toast.error('Failed to start assessment session');
//       return { success: false, error: error.message };
//     }
//   }, [setActiveMilitarySession, setMilitarySessionId, setShowMilitaryVideoCall,
//     setIsMilitaryCallMinimized, setVideoCallSelectedUser]);

//   /**
//    * Handle joining military session from invitation
//    */
//   const handleJoinMilitarySession = useCallback((pendingSession) => {
//     if (!pendingSession) return false;

//     setActiveMilitarySession({
//       session_id: pendingSession.session_id,
//       program_id: pendingSession.program_id,
//       week_number: pendingSession.week_number
//     });
//     setMilitarySessionId(pendingSession.session_id);
//     activeSessionIdRef.current = pendingSession.session_id;

//     setVideoCallSelectedUser({
//       first_name: 'Assessment',
//       last_name: `Week ${pendingSession.week_number}`,
//       user_id: user?.id
//     });

//     setShowMilitaryVideoCall(true);
//     setIsMilitaryCallMinimized(false);
//     setShowMilitarySessionConfirm(false);
//     setPendingMilitarySession(null);

//     return true;
//   }, [user, setActiveMilitarySession, setMilitarySessionId, setShowMilitaryVideoCall,
//     setIsMilitaryCallMinimized, setVideoCallSelectedUser, setShowMilitarySessionConfirm,
//     setPendingMilitarySession]);

//   /**
//    * Handle military session completion
//    */
//   const handleMilitarySessionComplete = useCallback(async (result) => {
//     console.log("✅ Military session completed:", result);

//     setShowMilitaryVideoCall(false);
//     setMilitarySessionId(null);
//     setActiveMilitarySession(null);
//     activeSessionIdRef.current = null;
//     setIsMilitaryCallMinimized(false);

//     if (result.program_completed) {
//       toast.success('🎉 Assessment Program Completed!');
//     } else {
//       toast.success(`Week ${result.completed_week || '?'} completed!`);
//     }

//     if (selectedTeam && fetchTeamMembers) {
//       fetchTeamMembers(selectedTeam.team_id);
//     }

//     return result;
//   }, [selectedTeam, fetchTeamMembers, setShowMilitaryVideoCall, setMilitarySessionId,
//     setActiveMilitarySession, setIsMilitaryCallMinimized]);

//   /**
//    * Handle closing military session (without completing)
//    */
//   const handleMilitarySessionClose = useCallback(() => {
//     // console.log("🧹 Closing military session modal");

//     setShowMilitaryVideoCall(false);
//     setMilitarySessionId(null);
//     setActiveMilitarySession(null);
//     activeSessionIdRef.current = null;
//     setIsMilitaryCallMinimized(false);
//     setVideoCallSelectedUser(null);

//     if (selectedTeam && fetchTeamMembers) {
//       fetchTeamMembers(selectedTeam.team_id);
//     }
//   }, [selectedTeam, fetchTeamMembers, setShowMilitaryVideoCall, setMilitarySessionId,
//     setActiveMilitarySession, setIsMilitaryCallMinimized, setVideoCallSelectedUser]);

//   /**
//    * Abandon session API call
//    * FIX: ended_at removed from server — this just calls the fixed endpoint
//    */
//   const abandonSession = useCallback(async (sessionId) => {
//     if (!sessionId) return { success: false };
//     try {
//       const response = await axios.post(
//         `${config.baseURL}/api/military/sessions/${sessionId}/abandon`,
//         {},
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       return { success: true, data: response.data };
//     } catch (error) {
//       console.error('Error abandoning session:', error);
//       return { success: false, error: error.message };
//     }
//   }, [token]);

//   return {
//     activeSessionIdRef,
//     isEndingSessionRef,
//     checkUserActiveMilitarySession,
//     handleStartMilitarySession,
//     handleStartMilitaryFromAddModal,
//     handleJoinMilitarySession,
//     handleMilitarySessionComplete,
//     handleMilitarySessionClose,
//     abandonSession,
//   };
// };